import { logger } from '../utils/logger'
import { HttpClient } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { concatMap, from, identity, map, Observable, retry, startWith, tap, throwIfEmpty, timeout } from 'rxjs'
import { UnespCoreEnvironment } from 'src/libs/unesp-core'

import { Inscricao } from '../models/inscricao'
import { SigadDadosBasicos } from '../models/sigad-protocolo'
import { ErrorHandlerService } from './error-handler.service'
import { InscricaoService } from './inscricao.service'

@Injectable({
  providedIn: 'root',
})
export class SigadService {
  private baseUrl: string
  private isDebug: boolean

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private inscricaoService: InscricaoService,
    @Inject('environment') env: UnespCoreEnvironment
  ) {
    this.baseUrl = env.baseUrlApi
    this.isDebug = !env.production
  }

  getProtocolo(concursoId: number): Observable<SigadDadosBasicos> {
    return this.http
      .get<SigadDadosBasicos>(`${this.baseUrl}/inscricao/sigad/protocolo`, { params: { concursoId } })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  verificarDisponibilidade(): Observable<{ disponivel: boolean; mensagem: string | null }> {
    return this.http
      .get<{ disponivel: boolean; mensagem: string | null }>(`${this.baseUrl}/inscricao/sigad/disponibilidade`)
  }

  enviarDocumentosPorInscricao(inscricaoId: number) {
    return this.http
      .post(`${this.baseUrl}/inscricao/sigad/documentos`, { inscricaoId })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  enviarClassificacaoFase2(concursoId: number) {
    return this.http
      .post<string[]>(`${this.baseUrl}/inscricao/sigad/classificacao/fase2`, {}, { params: { concursoId } })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  enviarClassificacaoFase1(concursoId: number) {
    return this.http
      .post<string[]>(`${this.baseUrl}/inscricao/sigad/classificacao/fase1`, {}, { params: { concursoId } })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  getClassificacaoFase1PdfUrl(concursoId: number): string {
    return `${this.baseUrl}/inscricao/sigad/classificacao/fase1/pdf?concursoId=${concursoId}`
  }

  getClassificacaoFase2PdfUrl(concursoId: number): string {
    return `${this.baseUrl}/inscricao/sigad/classificacao/fase2/pdf?concursoId=${concursoId}`
  }

  enviarDocumentos(concursoId: number): Observable<{ current: number; total: number }> {
    return from(this.inscricaoService.listar(concursoId, '', '', 0, '', 0, 0)).pipe(
      concatMap(({ content: inscricoes }: { content: Inscricao[] }) => {
        const total = inscricoes.length

        return from(inscricoes).pipe(
          concatMap((item, index) =>
            from(this.enviarDocumentosPorInscricao(item.id)).pipe(
              map(() => ({ current: index + 1, total })),
              throwIfEmpty(() => new Error('No data returned (EMPTY)!'))
            )
          ),
          startWith({ current: 0, total })
        )
      })
    )
  }

  enviarDeclaracoes(concursoId: number) {
    return this.http
      .post<string[]>(`${this.baseUrl}/comissao/${concursoId}/declaracao/atualizar-sigad`, {})
      .pipe(identity, this.errorHandlerService.catchError())
  }

  verificarIntegridade(concursoId: number, categoria: string): Observable<any> {
    return this.http
      .post<any>(`${this.baseUrl}/inscricao/sigad/verificar-integridade`, {}, { params: { concursoId, categoria } })
      .pipe(
        timeout(60000),
        retry(2),
        tap(res => {
          if (this.isDebug) {
            logger.log('[SIGAD-INTEGRIDADE]', categoria, res)
          }
        }),
      )
  }
}
